import React from 'react'
import clsx from 'clsx'
import moment from 'moment'
import PropTypes from 'prop-types'
import { filter, isEmpty, isNil } from 'ramda'
import { useIntl, FormattedMessage } from 'react-intl'
import { Typography, AppBar, makeStyles } from '@material-ui/core'

import { checkDraft } from 'commons/timelineAction'
import { UNSENT } from 'app/constants/messageDetail'
import { EVENT_STATUS_CANCELED } from 'app/constants/messages'
import { useAppSettings, getSettingByKey } from 'commons/useAppSettings'
import { TYPE_GENERAL, TYPE_EVENT, TYPE_POLL } from 'app/constants/messageType'

import { PaidPlanAccess } from 'app/auth/paidPlanAccess'
import PaidPlanAccessBlockV4 from 'app/auth/PaidPlanAccessBlockV4'

import Topic from 'app/components/Topic'

import Channels from 'app/componentV4/Channel/Channels'
import Gallery from 'app/componentV4/MessageDetail/Gallery'
import Recipients from 'app/componentV4/MessageDetail/Recipients'
import Voucher from 'app/componentV4/MessageDetail/Voucher/Voucher'
import MessageType from 'app/componentV4/MessageDetail/MessageType'
import PollDetails from 'app/componentV4/MessageDetail/Poll/PollDetails'
import EventDetail from 'app/componentV4/MessageDetail/Event/EventDetail'
import MessagePriority from 'app/componentV4/MessageDetail/MessagePriority'
import UnsentDate from 'app/componentV4/MessageDetail/InformationDate/UnsentDate'
import PollListOnDetail from 'app/componentV4/MessageDetail/Poll/PollListOnDetail'
import TextToSpeech from 'app/componentV4/MessageDetail/TextToSpeech/TextToSpeech'
import ReminderDate from 'app/componentV4/MessageDetail/InformationDate/ReminderDate'
import ScheduledDate from 'app/componentV4/MessageDetail/InformationDate/ScheduledDate'
import ExpirationDate from 'app/componentV4/MessageDetail/InformationDate/ExpirationDate'
import ContentGeneralEvent from 'app/componentV4/MessageDetail/Content/ContentGeneralEvent'

import useAction from 'app/main/messages/timeline/Action/useAction'
import ToggleApplyWebHistory from 'app/main/messages/timeline/Action/ToggleApplyWebHistory'

import CommentsCollapse from './Comment/CommentsCollapse'
import { getLocalizedDate } from 'commons/date'
import { getSubGroupOptions } from 'commons/groupLogic'

const useStyles = makeStyles((theme) => ({
  content: {
    height: '87vh',
    overflowY: 'auto',
  },
  bottomBar: {
    boxShadow: 'unset',
    height: '48px',
    top: 'auto',
    bottom: 0,
    padding: '.5rem 2.5rem',
    zIndex: 90,
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    [theme.breakpoints.up('sm')]: {
      width: '85%',
    },
    [theme.breakpoints.up('md')]: {
      width: '60%',
    },
    [theme.breakpoints.up('lg')]: {
      width: '40%',
    },
  },
}))

const MessageDetail = (props) => {
  const {
    value,
    onFetch,
    isComment,
    languageId,
    isLoadContent,
    isOpenMessageDetail,
  } = props
  const classes = useStyles()
  const { formatMessage } = useIntl()

  const { cancelSchedule, cancelReminder, cancelExpiration } =
    useAction(onFetch)

  let currentMusicPlaying = []
  const isHideTtsHistory = Number(
    getSettingByKey('hide_tts_history')(useAppSettings()),
  )
  const isShowTTS =
    value.isTTSEnabled && !value.isDraft && isHideTtsHistory !== 1

  const formattedGroups = getSubGroupOptions(formatMessage, value.groups)

  return (
    <>
      <div
        className={clsx('p-10 divide-y divide-neutrals-200', classes.content)}
      >
        <div className="flex justify-between mb-10 ">
          <div className="text-primary space-y-3 w-full">
            {/* From Type Priority */}
            <div className="flex justify-between items-center">
              <Typography className="text-14 font-normal text-primary">
                <FormattedMessage
                  id="timeline.detail.message.from"
                  values={{
                    fullName: (
                      <span
                        className={
                          !isNil(value.creator.deletedAt) ? 'line-through' : ''
                        }
                      >
                        {`${value.creator.firstName} ${value.creator.lastName}`}
                      </span>
                    ),
                    b: (text) => <b>{text}</b>,
                  }}
                />
              </Typography>
              <div className="space-x-3 flex items-center">
                <MessageType
                  type={value.type}
                  isReminder={value.isReminder}
                  fill={'#183560'}
                  tooltip={true}
                />
                <MessagePriority level={value.level} />
              </div>
            </div>
            {/* To */}
            <div className="flex">
              <Typography className="text-14 font-normal text-primary">
                <FormattedMessage id="timeline.detail.message.to" />
              </Typography>
              &nbsp;
              <Recipients
                groups={formattedGroups}
                recipients={filter(
                  (recipient) => !isNil(recipient.firstName),
                  value.recipients,
                )}
                channels={value.channels}
                isDraft={checkDraft(value.status)}
                isScheduled={value.isScheduled}
                isTooltip={false}
              />
            </div>
            {/* Via */}
            <div className="flex">
              <Typography className="text-14 font-normal text-primary">
                <FormattedMessage id="timeline.detail.message.via" />
              </Typography>
              &nbsp;
              <Channels channels={value.channels} isTooltip={false} />
            </div>
            {/* Date */}
            <Typography className="text-14 font-normal text-primary">
              <FormattedMessage
                id="timeline.detail.message.date"
                values={{
                  date: getLocalizedDate(value.createdAt, 'DATE'),
                  time: getLocalizedDate(value.createdAt, 'TIME'),
                  b: (text) => <b>{text}</b>,
                }}
              />
            </Typography>
            {value.status === UNSENT ? (
              <UnsentDate
                unsendDate={value.unsendDate}
                unsentBy={value.unsendBy}
              />
            ) : null}
            {!isNil(value.expiration) ? (
              <ExpirationDate
                id={value.id}
                expirationDate={value?.expiration?.date}
                isAction={true}
                onCancelExpiration={cancelExpiration}
              />
            ) : null}
            {value.isScheduled ? (
              <ScheduledDate
                id={value.id}
                scheduledDate={value?.schedule?.date}
                scheduleCanceledDate={value.scheduleCanceledDate}
                scheduleCancelBy={value.scheduleCancelBy}
                isAction={true}
                onCancelSchedule={cancelSchedule}
              />
            ) : null}
            {value.isReminder ? (
              <ReminderDate
                id={value.id}
                reminderDate={value?.reminder?.date ?? null}
                sentDate={value.sendDate}
                isAction={true}
                onCancelReminder={cancelReminder}
              />
            ) : null}
          </div>
        </div>
        {isNil(value.event) ? null : (
          <div className="py-5 space-y-3">
            <Typography
              component="p"
              className="text-primary text-16 font-bold"
            >
              {formatMessage({
                id: 'timeline.detail.message.event.detail.title',
              })}
            </Typography>
            <EventDetail
              id={value.id}
              data={value.event}
              isPropose={!value.isDraft}
              isEventCanceled={
                value.isCanceled && value.event.status === EVENT_STATUS_CANCELED
              }
              canceledReason={value.canceledReason}
              isAction={false}
            />
          </div>
        )}
        {isNil(value.poll) ? null : (
          <div className="py-5 space-y-3">
            <Typography
              component="p"
              className="text-primary text-16 font-bold"
            >
              {formatMessage({
                id: 'timeline.detail.message.poll.detail.title',
              })}
            </Typography>
            <PollDetails data={value.poll} />
          </div>
        )}
        <div className="py-10 space-y-10">
          {value.type === TYPE_POLL && (
            <PollListOnDetail
              notiId={value.id}
              pollId={value.poll.id}
              languageId={languageId}
            />
          )}
          {[TYPE_GENERAL, TYPE_EVENT].includes(value.type) && (
            <ContentGeneralEvent value={value} isLoading={isLoadContent} />
          )}
          {isEmpty(value?.topics) || isLoadContent ? null : (
            <div dir="auto" className="w-full">
              {value?.topics.map((topic, index) => (
                <Topic
                  id={index}
                  key={index}
                  name={topic}
                  style={{
                    icon: 'max-w-16 h-auto',
                    text: 'text-14 mr-3',
                  }}
                />
              ))}
            </div>
          )}
          {isShowTTS ? (
            <TextToSpeech
              id={value.id}
              isLoadingAudio={isLoadContent}
              languageId={value.translation.languageId}
              currentMusicPlaying={currentMusicPlaying}
              audioUrl={isLoadContent ? null : value.audioUrl}
              isStopAudio={!isOpenMessageDetail || isLoadContent}
            />
          ) : null}
        </div>

        {isEmpty(value.medias) ? null : <Gallery data={value.medias} />}

        {isNil(value.voucher) ? null : (
          <Voucher data={value.voucher} isLoading={isLoadContent} />
        )}

        <div className="py-5 mb-36">
          <PaidPlanAccess
            freePlanComponent={
              <div className="flex items-center py-5">
                <PaidPlanAccessBlockV4 titleId="paidPlanRestriction.commentTranslationMetric.description.text" />
              </div>
            }
          >
            <CommentsCollapse
              id={value.id}
              isExplain={isComment}
              onFetchTimelineWithoutLoader={onFetch}
            />
          </PaidPlanAccess>
        </div>
      </div>
      {value.isSharingEnabled && !isNil(value.publicLink) ? (
        <AppBar position="fixed" className={classes.bottomBar}>
          <ToggleApplyWebHistory id={value.id} value={value.isDisplayHistory} />
        </AppBar>
      ) : null}
    </>
  )
}

const dataPropTypes = {
  id: PropTypes.number,
  type: PropTypes.number,
  level: PropTypes.number,
  isScheduled: PropTypes.bool,
  isDraft: PropTypes.bool,
  isCanceled: PropTypes.bool,
  isReminder: PropTypes.bool,
  isTTSEnabled: PropTypes.bool,
  isSharingEnabled: PropTypes.bool,
  isDisplayHistory: PropTypes.bool,
  timezone: PropTypes.string,
  channels: PropTypes.arrayOf(PropTypes.number),
  translation: PropTypes.shape({
    title: PropTypes.string,
    descriptionPreview: PropTypes.string,
    body: PropTypes.string,
    languageId: PropTypes.number,
    languageCode: PropTypes.string,
  }),
  audioUrl: PropTypes.string,
  publicLink: PropTypes.string,
  status: PropTypes.string,
  medias: PropTypes.arrayOf(
    PropTypes.shape({
      mediaId: PropTypes.number,
      type: PropTypes.string,
      fileName: PropTypes.string,
      url: PropTypes.string,
      socialType: PropTypes.string,
      isSelected: PropTypes.number,
    }),
  ),
  recipients: PropTypes.arrayOf(
    PropTypes.shape({
      subscriberId: PropTypes.number,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      avatar: PropTypes.string,
    }),
  ),
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      groupId: PropTypes.number,
      groupType: PropTypes.number,
      groupName: PropTypes.string,
      tagId: PropTypes.number,
      name: PropTypes.string,
    }),
  ),
  voucher: PropTypes.shape({
    voucherId: PropTypes.number,
    templateId: PropTypes.number,
    status: PropTypes.number,
    startTime: PropTypes.string,
    endTime: PropTypes.string,
    isDraft: PropTypes.number,
    isScheduled: PropTypes.number,
    isActive: PropTypes.number,
    translated: PropTypes.shape({
      title: PropTypes.string,
      body: PropTypes.string,
    }),
    template: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      imageUrl: PropTypes.string,
      fontColor: PropTypes.string,
    }),
  }),
  sendDate: PropTypes.string,
  unsendDate: PropTypes.string,
  unsendBy: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }),
  scheduleCanceledDate: PropTypes.string,
  canceledReason: PropTypes.string,
  expiration: PropTypes.shape({
    date: PropTypes.string,
    seconds: PropTypes.number,
  }),
  schedule: PropTypes.shape({
    date: PropTypes.string,
    timezone: PropTypes.string,
  }),
  reminder: PropTypes.shape({
    date: PropTypes.string,
    timezone: PropTypes.string,
  }),
  event: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    startTimeZone: PropTypes.string,
    endTimeZone: PropTypes.string,
    status: PropTypes.number,
    location: PropTypes.shape({
      latitude: PropTypes.number,
      longitude: PropTypes.number,
    }),
    actionsCount: PropTypes.shape({
      going: PropTypes.number,
      maybe: PropTypes.number,
      notGoing: PropTypes.number,
    }),
  }),
  scheduleCancelBy: PropTypes.shape({
    id: PropTypes.number,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
  }),
  createdAt: PropTypes.string,
  creator: PropTypes.shape({
    id: PropTypes.number,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    deletedAt: PropTypes.string,
  }),
  topics: PropTypes.arrayOf(PropTypes.string),
  poll: PropTypes.shape({
    id: PropTypes.number,
    type: PropTypes.string,
    status: PropTypes.string,
    endDate: PropTypes.string,
    isAnonymous: PropTypes.bool,
    optionCount: PropTypes.number,
    endDateTimeZone: PropTypes.string,
  }),
}
MessageDetail.propTypes = {
  isDirty: PropTypes.bool,
  onFetch: PropTypes.func,
  isComment: PropTypes.bool,
  languageId: PropTypes.number,
  isLoadContent: PropTypes.bool,
  isOpenMessageDetail: PropTypes.bool,
  isLoadingPollDetail: PropTypes.bool,
  value: PropTypes.shape(dataPropTypes).isRequired,
}

export default MessageDetail
